import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import Card from '../components/Card'
import api from '../services/api'
import { getToken, clearAuth } from '../utils/userData'

const Build3Interest = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    entrepreneurExperience: '',
    entrepreneurNeeds: '',
    onlineModalities: '',
    understandsGiftCulture: false,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    // Check if user is authenticated as facilitator
    const token = getToken()
    if (!token) {
      setError('Please log in as a facilitator to submit this form.')
      setCheckingAuth(false)
      return
    }

    // Verify facilitator authentication by checking profile
    const verifyAuth = async () => {
      try {
        await api.get('/api/facilitators/profile')
        setCheckingAuth(false)
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Please log in as a facilitator to submit this form.')
          clearAuth()
        } else {
          setError('Failed to verify authentication. Please try again.')
        }
        setCheckingAuth(false)
      }
    }

    verifyAuth()
  }, [])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    // Validate checkbox
    if (!formData.understandsGiftCulture) {
      setError('Please confirm that you understand this is a pilot program offered in gift culture.')
      setIsSubmitting(false)
      return
    }

    try {
      await api.post('/api/facilitators/build3-interest', formData)
      setSuccess(true)
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Failed to submit form. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  if (checkingAuth) {
    return (
      <div className="bg-cream min-h-screen py-8">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Verifying authentication...</p>
            </motion.div>
          </Card>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="bg-cream min-h-screen py-8">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Thank you for your interest!
              </h2>
              <p className="text-gray-600 mb-6">
                Your submission has been received. We&apos;ll be in touch soon.
              </p>
              <button
                onClick={() => navigate(-1)}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                Back to Meetup
              </button>
            </motion.div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream min-h-screen py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2 transition-colors justify-center"
            >
              <span>←</span> Back
            </button>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Design an Energizing Wellness Routine for the{' '}
              <span className="text-primary-600">
                Build3 x Wellness Loop Program
              </span>
            </h1>
            <div className="max-w-3xl mx-auto text-lg text-gray-600 space-y-4">
              <p>
                Lisha is designing an 8-week inter modality wellness series
                called <strong>Energizer Sessions</strong> for the{' '}
                <a
                  href="https://www.build3.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Build3 Startup Accelerator
                </a>
                , whose current cohort includes 60+ entrepreneurs working on
                impact-driven ventures.
              </p>
              <p>
                The larger Build3 program runs for 10 weeks starting November
                17, and the Energizer Sessions will be integrated from Week 3
                onwards, with two online sessions each week (Tuesdays & Fridays,
                8–9 AM).
              </p>
              <p>
                We&apos;re inviting facilitators, therapists, and practitioners
                interested in designing this pilot program.
              </p>
            </div>
          </div>

          {/* Form */}
          <Card className="max-w-3xl mx-auto">
            {error && !isSubmitting && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                {error.includes('log in') && (
                  <div className="mt-2">
                    <button
                      onClick={() => navigate('/')}
                      className="text-red-700 underline font-semibold"
                    >
                      Go to Home
                    </button>
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">

              <div>
                <label
                  htmlFor="entrepreneurExperience"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  How much experience do you have in working with entrepreneurs?
                  And in what capacity?
                </label>
                <textarea
                  id="entrepreneurExperience"
                  name="entrepreneurExperience"
                  value={formData.entrepreneurExperience}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Please share your experience working with entrepreneurs..."
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="entrepreneurNeeds"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  What do you think entrepreneurs need most in their wellness
                  routine?
                </label>
                <textarea
                  id="entrepreneurNeeds"
                  name="entrepreneurNeeds"
                  value={formData.entrepreneurNeeds}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Share your thoughts on what entrepreneurs need..."
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="onlineModalities"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  What modalities do you think work best online? Any out of the
                  box ideas?
                </label>
                <textarea
                  id="onlineModalities"
                  name="onlineModalities"
                  value={formData.onlineModalities}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  placeholder="Share your ideas for online modalities..."
                  required
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="understandsGiftCulture"
                  name="understandsGiftCulture"
                  checked={formData.understandsGiftCulture}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="understandsGiftCulture"
                  className="text-sm text-gray-700"
                >
                  I understand that this is a pilot program and will be offered in gift culture,
                  with the potential to monetize in the future.{' '}
                  <span className="text-red-600">*</span>
                </label>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}

export default Build3Interest

